/**
 * Pure transforms for capturing reviewer feedback on NCCN evaluation items.
 *
 * Review data is stored in `ai_answer_feedback`, `reviewer_comments`, and the
 * per-criterion `reviewer_references` field. Chart captures live in
 * `reviewer_references`; free-text notes are `reviewer_comments` entries marked
 * with a {@link FEEDBACK_CONDITION} condition. Legacy PDF references encoded in
 * reviewer comments remain readable for compatibility.
 * @module nccnReviewFeedback
 */
import {
  NCCNEvaluationPayload,
  NCCNEvaluationRecord,
} from "../../../data-model/nccnEvaluation";
import {
  EvaluationItem,
  EvaluationResult,
  EvidenceFact,
  FeedbackType,
  HumanReview,
  HumanVerdict,
  ReviewerReference,
  UserComment,
} from "../../../data-model/nccnGuidelines";
import { PdfProof } from "../types/pdfProof";

/** `reviewer_comments.condition` markers used to encode the feedback kind. */
export const FEEDBACK_CONDITION = {
  INCORRECT_LOGIC: "incorrect_logic",
  INCORRECT_EVIDENCE: "incorrect_evidence",
  INCORRECT_REFERENCE: "incorrect_reference",
  INCORRECT_COMMENT: "incorrect_comment",
} as const;

/** Which feedback form the reviewer used. */
export type FeedbackKind =
  | "incorrect_logic"
  | "incorrect_evidence"
  | "incorrect_reference"
  | "incorrect_comment";

/**
 * Map from a flagged evidence fact's text to the reviewer's correction for it.
 * Encoded as JSON in the `incorrect_evidence` reviewer comment so per-evidence
 * corrections can be persisted without any backend model change.
 */
export type EvidenceCorrections = Record<string, string>;

/**
 * Input describing a reviewer's feedback action on a single evaluation item.
 */
export type FeedbackInput =
  | { type: "correct" }
  /**
   * Clears any stored verdict, returning the criterion to unreviewed. Emitted
   * when the incorrect form holds no references, since an incorrect verdict is
   * only valid with at least one captured annotation.
   */
  | { type: "unreviewed" }
  | { type: "incorrect_logic"; correctLogic: string }
  | {
      type: "incorrect_evidence";
      flagged: string[];
      corrections: EvidenceCorrections;
    }
  | { type: "incorrect_reference"; proofs: PdfProof[] }
  /**
   * Free-text note the reviewer typed instead of attaching a chart reference.
   * Stored as a `reviewer_comments` entry, so it needs no PDF region.
   */
  | { type: "incorrect_comment"; comment: string };

/** Parsed view of the feedback currently stored on an item. */
export interface ParsedItemFeedback {
  /** Whether the reviewer marked the AI answer correct (thumbs up). */
  isCorrect: boolean;
  /** Whether the reviewer marked the AI answer incorrect (thumbs down). */
  isIncorrect: boolean;
  /** The stored feedback kind, or null when none. */
  kind: FeedbackKind | null;
  /** Corrected logic text (for the incorrect-logic kind). */
  correctLogic: string;
  /** Evidence strings flagged as incorrect (for the incorrect-evidence kind). */
  flagged: string[];
  /**
   * Per-evidence corrections keyed by flagged fact text (incorrect-evidence
   * kind). Missing keys mean the reviewer flagged a fact without a correction.
   */
  corrections: EvidenceCorrections;
  /** PDF text selections and screenshot references attached to new feedback. */
  proofs: PdfProof[];
  /** Free-text note (for the incorrect-comment kind). */
  comment: string;
}

/**
 * Removes any feedback-encoding comments, leaving unrelated comments intact.
 *
 * @param comments - Existing reviewer comments on an item
 * @returns Comments with the feedback markers stripped
 */
const stripFeedbackComments = (comments: UserComment[]): UserComment[] =>
  comments.filter(
    (comment) =>
      comment.condition !== FEEDBACK_CONDITION.INCORRECT_LOGIC &&
      comment.condition !== FEEDBACK_CONDITION.INCORRECT_EVIDENCE &&
      comment.condition !== FEEDBACK_CONDITION.INCORRECT_REFERENCE &&
      comment.condition !== FEEDBACK_CONDITION.INCORRECT_COMMENT,
  );

/**
 * Builds a reviewer comment carrying encoded feedback.
 *
 * @param condition - Feedback-kind marker
 * @param comment - Correction payload (plain text or JSON)
 * @param userId - ID of the reviewer
 * @returns A populated {@link UserComment}
 */
const makeComment = (
  condition: string,
  comment: string,
  userId: string,
): UserComment => ({
  comment,
  condition,
  updated_at: new Date().toISOString(),
  updated_by: userId,
});

/**
 * Serialises per-evidence corrections into the string stored on the
 * `incorrect_evidence` reviewer comment. Only flagged facts with a non-empty
 * correction are kept, so the encoded payload stays minimal.
 *
 * @param corrections - Map of flagged fact text to its correction
 * @returns A JSON string encoding the (non-empty) corrections map
 */
const encodeCorrections = (corrections: EvidenceCorrections): string => {
  const trimmed = Object.entries(corrections).reduce<EvidenceCorrections>(
    (accumulator, [fact, correction]) => {
      const value = correction.trim();
      if (value.length > 0) accumulator[fact] = value;
      return accumulator;
    },
    {},
  );
  return JSON.stringify(trimmed);
};

/**
 * Parses the corrections map from a stored `incorrect_evidence` comment.
 *
 * Accepts both the current JSON map encoding and the legacy plain-text
 * encoding (a single shared correction), which is applied to every flagged
 * fact so previously saved feedback keeps rendering.
 *
 * @param comment - The stored comment string
 * @param flagged - Texts of the facts currently flagged as incorrect
 * @returns The decoded per-evidence corrections map
 */
const decodeCorrections = (
  comment: string,
  flagged: string[],
): EvidenceCorrections => {
  try {
    const parsed = JSON.parse(comment);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return Object.entries(
        parsed as Record<string, unknown>,
      ).reduce<EvidenceCorrections>((accumulator, [fact, correction]) => {
        if (typeof correction === "string") accumulator[fact] = correction;
        return accumulator;
      }, {});
    }
  } catch {
    // Not JSON: fall through to legacy plain-text handling below.
  }

  const legacy = comment.trim();
  if (legacy.length === 0) return {};
  return flagged.reduce<EvidenceCorrections>((accumulator, fact) => {
    accumulator[fact] = legacy;
    return accumulator;
  }, {});
};

/**
 * Whether a value looks like a captured PDF page region.
 *
 * @param region - Candidate region from persisted or encoded proof data
 * @returns True when `pageIndex` and a rect with origin/size are present
 */
const isPdfProofRegion = (region: unknown): region is PdfProof["region"] => {
  if (!region || typeof region !== "object") return false;
  const candidate = region as PdfProof["region"];
  return (
    typeof candidate.pageIndex === "number" &&
    !!candidate.rect &&
    typeof candidate.rect.origin?.x === "number" &&
    typeof candidate.rect.origin?.y === "number" &&
    typeof candidate.rect.size?.width === "number" &&
    typeof candidate.rect.size?.height === "number"
  );
};

/**
 * Parses PDF references from an encoded reviewer comment.
 *
 * @param comment - JSON string stored for incorrect-reference feedback
 * @returns Valid-looking PDF references, or an empty array for malformed data
 */
const decodePdfProofs = (comment: string): PdfProof[] => {
  try {
    const parsed: unknown = JSON.parse(comment);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (proof): proof is PdfProof =>
        !!proof &&
        typeof proof === "object" &&
        typeof (proof as PdfProof).id === "string" &&
        ((proof as PdfProof).type === "pdf_text" ||
          (proof as PdfProof).type === "pdf_screenshot") &&
        isPdfProofRegion((proof as PdfProof).region),
    );
  } catch {
    return [];
  }
};

/**
 * Converts a PDF capture into the API's persisted reviewer-reference shape.
 *
 * @param proof - Confirmed PDF capture from the review UI
 * @returns Reviewer reference using API field names and capture types
 */
const toReviewerReference = (proof: PdfProof): ReviewerReference => ({
  id: proof.id,
  type: proof.type === "pdf_text" ? "text" : "screenshot",
  text: proof.text ?? null,
  image_path: proof.imagePath ?? null,
  region: {
    page_index: proof.region.pageIndex,
    rect: {
      x: proof.region.rect.origin.x,
      y: proof.region.rect.origin.y,
      width: proof.region.rect.size.width,
      height: proof.region.rect.size.height,
    },
  },
  comment: proof.comment ?? null,
});

/**
 * Converts a persisted reviewer-reference region into the PDF capture UI shape.
 *
 * @param region - Page index and rectangle stored on a reviewer reference
 * @returns Region using camelCase PDF proof fields
 */
const toPdfProofRegion = (
  region: ReviewerReference["region"],
): PdfProof["region"] => ({
  pageIndex: region.page_index,
  rect: {
    origin: { x: region.rect.x, y: region.rect.y },
    size: {
      width: region.rect.width,
      height: region.rect.height,
    },
  },
});

/**
 * Converts a persisted reviewer reference into the PDF capture UI shape.
 *
 * @param reference - Reviewer reference returned in an evaluation item
 * @returns PDF proof suitable for the capture form and summary components
 */
const toPdfProof = (reference: ReviewerReference): PdfProof => ({
  id: reference.id,
  type: reference.type === "text" ? "pdf_text" : "pdf_screenshot",
  text: reference.text ?? undefined,
  imagePath: reference.image_path ?? undefined,
  region: toPdfProofRegion(reference.region),
  comment: reference.comment ?? undefined,
});

/**
 * Marks each fact's `user_validation` according to the flagged set: a fact is
 * invalidated (`user_validation: false`) when its text appears in `flagged`,
 * and validated (`true`) otherwise. Passing an empty `flagged` resets every
 * fact back to valid.
 *
 * @param facts - The evidence facts to update
 * @param flagged - Texts of facts the reviewer flagged as incorrect
 * @returns A new array of facts with `user_validation` applied
 */
const applyFactValidation = (
  facts: EvidenceFact[],
  flagged: string[],
): EvidenceFact[] =>
  facts.map((fact) => ({
    ...fact,
    user_validation: !flagged.includes(fact.text),
  }));

/**
 * Returns a new item with the reviewer feedback applied.
 *
 * @param item - The evaluation item to update
 * @param input - The reviewer feedback action
 * @param userId - ID of the reviewer
 * @returns A new {@link EvaluationItem} with feedback encoded
 */
const updateItem = (
  item: EvaluationItem,
  input: FeedbackInput,
  userId: string,
): EvaluationItem => {
  const comments = stripFeedbackComments(item.reviewer_comments ?? []);

  if (input.type === "unreviewed") {
    return {
      ...item,
      ai_answer_feedback: null,
      supportive_facts: applyFactValidation(item.supportive_facts ?? [], []),
      contradictory_facts: applyFactValidation(
        item.contradictory_facts ?? [],
        [],
      ),
      reviewer_comments: comments,
      reviewer_references: [],
    };
  }

  if (input.type === "correct") {
    return {
      ...item,
      ai_answer_feedback: FeedbackType.Like,
      supportive_facts: applyFactValidation(item.supportive_facts ?? [], []),
      contradictory_facts: applyFactValidation(
        item.contradictory_facts ?? [],
        [],
      ),
      reviewer_comments: comments,
      reviewer_references: [],
    };
  }

  if (input.type === "incorrect_logic") {
    return {
      ...item,
      ai_answer_feedback: FeedbackType.Dislike,
      supportive_facts: applyFactValidation(item.supportive_facts ?? [], []),
      contradictory_facts: applyFactValidation(
        item.contradictory_facts ?? [],
        [],
      ),
      reviewer_comments: [
        ...comments,
        makeComment(
          FEEDBACK_CONDITION.INCORRECT_LOGIC,
          input.correctLogic,
          userId,
        ),
      ],
      reviewer_references: [],
    };
  }

  if (input.type === "incorrect_reference") {
    return {
      ...item,
      ai_answer_feedback: FeedbackType.Dislike,
      supportive_facts: applyFactValidation(item.supportive_facts ?? [], []),
      contradictory_facts: applyFactValidation(
        item.contradictory_facts ?? [],
        [],
      ),
      reviewer_comments: comments,
      reviewer_references: input.proofs.map(toReviewerReference),
    };
  }

  if (input.type === "incorrect_comment") {
    return {
      ...item,
      ai_answer_feedback: FeedbackType.Dislike,
      supportive_facts: applyFactValidation(item.supportive_facts ?? [], []),
      contradictory_facts: applyFactValidation(
        item.contradictory_facts ?? [],
        [],
      ),
      reviewer_comments: [
        ...comments,
        makeComment(
          FEEDBACK_CONDITION.INCORRECT_COMMENT,
          input.comment,
          userId,
        ),
      ],
      reviewer_references: [],
    };
  }

  return {
    ...item,
    ai_answer_feedback: FeedbackType.Dislike,
    supportive_facts: applyFactValidation(
      item.supportive_facts ?? [],
      input.flagged,
    ),
    contradictory_facts: applyFactValidation(
      item.contradictory_facts ?? [],
      input.flagged,
    ),
    reviewer_comments: [
      ...comments,
      makeComment(
        FEEDBACK_CONDITION.INCORRECT_EVIDENCE,
        encodeCorrections(input.corrections),
        userId,
      ),
    ],
    reviewer_references: [],
  };
};

/**
 * Immutably applies feedback to a single item within the evaluation payload.
 *
 * @param payload - The current evaluation payload
 * @param guidelineIndex - Index of the guideline being reviewed
 * @param itemIndex - Index of the criterion (item) within the guideline
 * @param input - The reviewer feedback action
 * @param userId - ID of the reviewer
 * @returns A new payload with the item updated
 */
export const applyItemFeedback = (
  payload: NCCNEvaluationPayload,
  guidelineIndex: number,
  itemIndex: number,
  input: FeedbackInput,
  userId: string,
): NCCNEvaluationPayload => ({
  ...payload,
  guidelines: (payload.guidelines ?? []).map((guideline, gi) => {
    if (gi !== guidelineIndex) return guideline;
    const evaluation = guideline.metadata?.evaluation;
    if (!evaluation) return guideline;
    return {
      ...guideline,
      metadata: {
        ...guideline.metadata,
        evaluation: {
          ...evaluation,
          items: (evaluation.items ?? []).map((item, ii) =>
            ii === itemIndex ? updateItem(item, input, userId) : item,
          ),
        },
      },
    };
  }),
});

/**
 * Reads the feedback currently encoded on an item, for prefilling the form.
 *
 * @param item - The evaluation item to inspect
 * @returns The parsed feedback state
 */
export const readItemFeedback = (item: EvaluationItem): ParsedItemFeedback => {
  const isCorrect = item.ai_answer_feedback === FeedbackType.Like;
  const isIncorrect = item.ai_answer_feedback === FeedbackType.Dislike;
  const empty: ParsedItemFeedback = {
    isCorrect,
    isIncorrect,
    kind: null,
    correctLogic: "",
    flagged: [],
    corrections: {},
    proofs: [],
    comment: "",
  };

  if ((item.reviewer_references?.length ?? 0) > 0) {
    return {
      ...empty,
      kind: "incorrect_reference",
      proofs: (item.reviewer_references ?? []).map(toPdfProof),
    };
  }

  const feedback = [...(item.reviewer_comments ?? [])]
    .reverse()
    .find(
      (comment) =>
        comment.condition === FEEDBACK_CONDITION.INCORRECT_LOGIC ||
        comment.condition === FEEDBACK_CONDITION.INCORRECT_EVIDENCE ||
        comment.condition === FEEDBACK_CONDITION.INCORRECT_REFERENCE ||
        comment.condition === FEEDBACK_CONDITION.INCORRECT_COMMENT,
    );
  if (!feedback) return empty;

  if (feedback.condition === FEEDBACK_CONDITION.INCORRECT_LOGIC) {
    return {
      ...empty,
      kind: "incorrect_logic",
      correctLogic: feedback.comment,
    };
  }

  if (feedback.condition === FEEDBACK_CONDITION.INCORRECT_COMMENT) {
    return {
      ...empty,
      kind: "incorrect_comment",
      comment: feedback.comment,
    };
  }

  if (feedback.condition === FEEDBACK_CONDITION.INCORRECT_REFERENCE) {
    return {
      ...empty,
      kind: "incorrect_reference",
      proofs: decodePdfProofs(feedback.comment),
    };
  }

  const flagged = [
    ...(item.supportive_facts ?? []),
    ...(item.contradictory_facts ?? []),
  ]
    .filter((fact) => fact.user_validation === false)
    .map((fact) => fact.text);

  return {
    ...empty,
    kind: "incorrect_evidence",
    flagged,
    corrections: decodeCorrections(feedback.comment, flagged),
  };
};

/**
 * Reviewer's guideline-level review, captured on the per-guideline summary step.
 */
export interface GuidelineReviewInput {
  /** Human met/not-met verdict for the guideline, or null when unset. */
  verdict: HumanVerdict | null;
  /** Free-text reasoning behind the human verdict. */
  verdictReasoning: string;
  /** Notes on what would help the AI get this guideline right next time. */
  aiImprovementNotes: string;
}

/** An empty {@link GuidelineReviewInput}, used as the unset default. */
export const EMPTY_GUIDELINE_REVIEW: GuidelineReviewInput = {
  verdict: null,
  verdictReasoning: "",
  aiImprovementNotes: "",
};

/**
 * Reads the guideline-level human review stored on a guideline record.
 *
 * @param guideline - The guideline record to inspect, or undefined
 * @returns The parsed guideline review, defaulting to the empty review
 */
export const readGuidelineReview = (
  guideline: NCCNEvaluationRecord | undefined,
): GuidelineReviewInput => {
  const review = guideline?.metadata?.human_review;
  if (!review) return EMPTY_GUIDELINE_REVIEW;
  return {
    verdict: review.verdict ?? null,
    verdictReasoning: review.verdict_reasoning ?? "",
    aiImprovementNotes: review.ai_improvement_notes ?? "",
  };
};

/**
 * Whether the AI evaluated a guideline as "Met" (`final_result === "True"`).
 *
 * @param guideline - The guideline record to inspect, or undefined
 * @returns True when the AI verdict is "Met"
 */
export const isAiMet = (guideline: NCCNEvaluationRecord | undefined): boolean =>
  guideline?.metadata?.evaluation?.final_result === "True";

/**
 * Whether the AI evaluated a guideline as "Not Met" (`final_result === "False"`).
 *
 * @param guideline - The guideline record to inspect, or undefined
 * @returns True when the AI verdict is "Not Met"
 */
export const isAiNotMet = (
  guideline: NCCNEvaluationRecord | undefined,
): boolean => guideline?.metadata?.evaluation?.final_result === "False";

/**
 * Whether the AI could not conclusively evaluate a guideline
 * (`final_result === "Undetermined"`).
 *
 * @param guideline - The guideline record to inspect, or undefined
 * @returns True when the AI verdict is "Undetermined"
 */
export const isAiUndetermined = (
  guideline: NCCNEvaluationRecord | undefined,
): boolean => guideline?.metadata?.evaluation?.final_result === "Undetermined";

/**
 * Whether a guideline must be stepped through in the mandatory review flow.
 *
 * The reviewer is required to review both the guidelines the AI marked "Met"
 * and the ones the AI could not determine ("Undetermined"); only the guidelines
 * the AI confidently marked "Not Met" are optional and reviewed on demand via
 * the override flow.
 *
 * @param guideline - The guideline record to inspect, or undefined
 * @returns True when the guideline is part of the mandatory step-through
 */
export const isMandatoryGuideline = (
  guideline: NCCNEvaluationRecord | undefined,
): boolean => isAiMet(guideline) || isAiUndetermined(guideline);

/**
 * The effective met/not-met verdict used to group a guideline. Prefers the human
 * reviewer's verdict when present (so an overridden guideline is treated as its
 * reviewed verdict) and falls back to the AI verdict otherwise.
 *
 * @param guideline - The guideline record to inspect, or undefined
 * @returns True when the guideline should be treated as "Met"
 */
export const isEffectiveMet = (
  guideline: NCCNEvaluationRecord | undefined,
): boolean => {
  const humanVerdict = guideline?.metadata?.human_review?.verdict;
  if (humanVerdict === HumanVerdict.Met) return true;
  if (humanVerdict === HumanVerdict.NotMet) return false;
  return isAiMet(guideline);
};

/**
 * The next guideline the reviewer lands on after skipping the current one
 * from the review screen (override mode), or null when the skip is terminal
 * and should return to the summary screen.
 *
 * The chain is keyed on the AI verdict (`final_result`), not the human
 * review: the current guideline's AI result picks its group (AI-Met, or
 * AI-Not-Met / AI-Undetermined), and the target is the next guideline in
 * list order in the SAME group. Candidates are never a concluded
 * guideline — one that already carries a human verdict (reviewed normally,
 * overridden, or previously skipped) — regardless of its AI result.
 *
 * The groups never cross over: skipping the last unreviewed AI-Met guideline
 * returns to the summary rather than jumping to a Not Met / Undetermined one
 * (and vice versa).
 *
 * @param payload - The evaluation payload to scan, or null/undefined
 * @param afterIndex - Full-array index of the guideline being skipped
 * @returns The next skip target's full-array index, or null when none is left
 */
export const findNextSkipTargetIndex = (
  payload: NCCNEvaluationPayload | null | undefined,
  afterIndex: number,
): number | null => {
  const guidelines = payload?.guidelines ?? [];
  const inMetGroup =
    !!guidelines[afterIndex] && isAiMet(guidelines[afterIndex]);

  for (let index = afterIndex + 1; index < guidelines.length; index += 1) {
    const guideline = guidelines[index];
    // A recorded human verdict concludes the guideline (normal review,
    // override, or skip), so it is never a target.
    if (guideline.metadata?.human_review?.verdict) continue;
    if (isAiMet(guideline) === inMetGroup) return index;
  }
  return null;
};

/**
 * The full-array indices of the guidelines the reviewer must step through
 * initially: the AI-Met and AI-Undetermined guidelines. AI-Not-Met guidelines
 * are optional (reviewed on demand via the override flow) and excluded.
 *
 * Met indices come first (in list order) followed by the Undetermined indices
 * (in list order), so the mandatory pager visits the AI-Met guidelines before
 * the AI-Undetermined ones.
 *
 * @param payload - The evaluation payload to scan, or null/undefined
 * @returns Met-then-Undetermined guideline indices, each in list order
 */
export const getMandatoryGuidelineIndices = (
  payload: NCCNEvaluationPayload | null | undefined,
): number[] => {
  const guidelines = payload?.guidelines ?? [];
  const met: number[] = [];
  const undetermined: number[] = [];
  guidelines.forEach((guideline, index) => {
    if (isAiMet(guideline)) met.push(index);
    else if (isAiUndetermined(guideline)) undetermined.push(index);
  });
  return [...met, ...undetermined];
};

/**
 * Whether any guideline in the payload carries a Human "Met" verdict.
 *
 * One Human-Met guideline already settles the order as Met, so the remaining
 * guidelines are optional work and the reviewer can submit the review early.
 *
 * @param payload - The evaluation payload to scan, or null/undefined
 * @returns True when at least one guideline is Human Met
 */
export const hasHumanMetGuideline = (
  payload: NCCNEvaluationPayload | null | undefined,
): boolean =>
  (payload?.guidelines ?? []).some(
    (guideline) =>
      guideline.metadata?.human_review?.verdict === HumanVerdict.Met,
  );

/**
 * Whether every mandatory guideline (AI-Met and AI-Undetermined) carries a
 * Human "Not Met" verdict — the all-Not-Met early-submit branch. The mandatory
 * set must be non-empty: an order with only AI-Not-Met guidelines has no
 * mandatory review and is submitted through the override flow instead.
 *
 * @param payload - The evaluation payload to scan, or null/undefined
 * @returns True when the mandatory set is non-empty and all are Human Not Met
 */
export const allMandatoryHumanNotMet = (
  payload: NCCNEvaluationPayload | null | undefined,
): boolean => {
  const mandatoryIndices = getMandatoryGuidelineIndices(payload);
  if (mandatoryIndices.length === 0) return false;
  const guidelines = payload?.guidelines ?? [];
  return mandatoryIndices.every(
    (index) =>
      guidelines[index]?.metadata?.human_review?.verdict ===
      HumanVerdict.NotMet,
  );
};

/**
 * Whether the reviewer may submit the review before every mandatory guideline
 * is fully reviewed: as soon as one guideline is Human Met (the order is Met)
 * or every mandatory guideline is Human Not Met (the order is Not Met).
 *
 * This gates only the early "Review" affordance; it does not replace
 * {@link computeMandatoryReviewProgress}, which still drives the sticky
 * `review_submitted` flag and the progress indicator.
 *
 * @param payload - The evaluation payload to scan, or null/undefined
 * @returns True when the early-submit condition is met
 */
export const canSubmitMnReview = (
  payload: NCCNEvaluationPayload | null | undefined,
): boolean => hasHumanMetGuideline(payload) || allMandatoryHumanNotMet(payload);

/**
 * Maps each guideline's original list index to its 1-based position in the
 * grouped review summary: the effectively-Met guidelines first, then the
 * effectively-Not-Met ones, each group kept in original list order. "Effective"
 * uses the reviewer's human verdict when present and falls back to the AI
 * verdict (see {@link isEffectiveMet}), so an AI-Not-Met guideline the reviewer
 * overrode to Met is numbered within the Met group.
 *
 * This is the single source of truth for the `NN/NN` counter shown both on the
 * summary rows (the "outer" number) and on the detailed guideline header (the
 * "inner" number), so the two always agree for the same guideline.
 *
 * @param guidelines - All guidelines in the evaluation, in their original order
 * @returns Array where `positions[originalIndex]` is the 1-based display position
 */
export const getGuidelineDisplayPositions = (
  guidelines: NCCNEvaluationRecord[],
): number[] => {
  const met: number[] = [];
  const notMet: number[] = [];
  guidelines.forEach((guideline, index) => {
    if (isEffectiveMet(guideline)) met.push(index);
    else notMet.push(index);
  });
  const positions = new Array<number>(guidelines.length).fill(0);
  [...met, ...notMet].forEach((originalIndex, rank) => {
    positions[originalIndex] = rank + 1;
  });
  return positions;
};

/**
 * A position in the review pager: which guideline and which step within it
 * (`criteriaIndex === items.length` denotes that guideline's summary step).
 */
export interface ReviewStep {
  /** Index of the guideline. */
  guidelineIndex: number;
  /** Index of the criterion, or `items.length` for the summary step. */
  criteriaIndex: number;
}

/**
 * Finds the first review step whose feedback has not yet been saved.
 *
 * Walks guidelines in order: a criterion counts as reviewed once its
 * `ai_answer_feedback` is set, and a guideline's summary step counts as
 * reviewed once its `human_review.verdict` is set. Returns the first step
 * failing either check so the reviewer resumes exactly where work remains. When
 * every step is already reviewed (or there are no guidelines), returns the last
 * guideline's summary step (or `{0, 0}` when empty).
 *
 * @param payload - The evaluation payload to scan, or null/undefined
 * @returns The first unreviewed {@link ReviewStep}
 */
export const findFirstUnreviewedStep = (
  payload: NCCNEvaluationPayload | null | undefined,
): ReviewStep => {
  const guidelines = payload?.guidelines ?? [];

  for (
    let guidelineIndex = 0;
    guidelineIndex < guidelines.length;
    guidelineIndex += 1
  ) {
    const items = guidelines[guidelineIndex]?.metadata?.evaluation?.items ?? [];

    const firstUnreviewedCriterion = items.findIndex(
      (item) => !item.ai_answer_feedback,
    );
    if (firstUnreviewedCriterion !== -1) {
      return { guidelineIndex, criteriaIndex: firstUnreviewedCriterion };
    }

    if (!guidelines[guidelineIndex]?.metadata?.human_review?.verdict) {
      return { guidelineIndex, criteriaIndex: items.length };
    }
  }

  if (guidelines.length === 0) {
    return { guidelineIndex: 0, criteriaIndex: 0 };
  }

  const lastGuidelineIndex = guidelines.length - 1;
  const lastItems =
    guidelines[lastGuidelineIndex]?.metadata?.evaluation?.items ?? [];
  return {
    guidelineIndex: lastGuidelineIndex,
    criteriaIndex: lastItems.length,
  };
};

/**
 * Like {@link findFirstUnreviewedStep} but scoped to the mandatory guidelines
 * only (AI-Met and AI-Undetermined). Not-Met guidelines are reviewed on demand
 * via the override flow, so they never block the initial step-through. Returns
 * full-array indices. A mandatory guideline already carrying a
 * `human_review.verdict` is concluded (reviewed normally or skipped) and is
 * skipped, so the pager resumes at the first still-open guideline. When every
 * mandatory guideline is concluded (or there are none), returns the last
 * mandatory guideline's summary step (or `{0, 0}` when the order has no
 * mandatory guidelines at all).
 *
 * @param payload - The evaluation payload to scan, or null/undefined
 * @returns The first unreviewed mandatory {@link ReviewStep}
 */
export const findFirstUnreviewedMandatoryStep = (
  payload: NCCNEvaluationPayload | null | undefined,
): ReviewStep => {
  const guidelines = payload?.guidelines ?? [];
  const mandatoryIndices = getMandatoryGuidelineIndices(payload);

  for (const guidelineIndex of mandatoryIndices) {
    const guideline = guidelines[guidelineIndex];
    // A recorded human verdict concludes the guideline (normal review or
    // skip), so it is never landed on during resume.
    if (guideline?.metadata?.human_review?.verdict) continue;

    const items = guideline?.metadata?.evaluation?.items ?? [];

    const firstUnreviewedCriterion = items.findIndex(
      (item) => !item.ai_answer_feedback,
    );
    if (firstUnreviewedCriterion !== -1) {
      return { guidelineIndex, criteriaIndex: firstUnreviewedCriterion };
    }

    return { guidelineIndex, criteriaIndex: items.length };
  }

  if (mandatoryIndices.length === 0) {
    return { guidelineIndex: 0, criteriaIndex: 0 };
  }

  const lastMandatoryIndex = mandatoryIndices[mandatoryIndices.length - 1];
  const lastItems =
    guidelines[lastMandatoryIndex]?.metadata?.evaluation?.items ?? [];
  return {
    guidelineIndex: lastMandatoryIndex,
    criteriaIndex: lastItems.length,
  };
};

/**
 * Whether a guideline is fully reviewed: every criterion has feedback saved
 * (`ai_answer_feedback` set) and the guideline-level summary verdict is set.
 *
 * @param guideline - The guideline record to inspect, or undefined
 * @returns True when the guideline has no remaining review work
 */
export const isGuidelineFullyReviewed = (
  guideline: NCCNEvaluationRecord | undefined,
): boolean => {
  const items = guideline?.metadata?.evaluation?.items ?? [];
  const allCriteriaReviewed = items.every((item) => !!item.ai_answer_feedback);
  const hasVerdict = !!guideline?.metadata?.human_review?.verdict;
  return allCriteriaReviewed && hasVerdict;
};

/**
 * Whether any review work has begun on a guideline: at least one criterion has
 * feedback saved (`ai_answer_feedback` set) or the guideline-level verdict is
 * set. Used to distinguish an untouched evaluation (show the "start" affordance)
 * from one that is partway through review (show progress).
 *
 * @param guideline - The guideline record to inspect, or undefined
 * @returns True when at least one review action has been recorded
 */
export const isGuidelineReviewStarted = (
  guideline: NCCNEvaluationRecord | undefined,
): boolean => {
  const items = guideline?.metadata?.evaluation?.items ?? [];
  const anyCriterionReviewed = items.some((item) => !!item.ai_answer_feedback);
  const hasVerdict = !!guideline?.metadata?.human_review?.verdict;
  return anyCriterionReviewed || hasVerdict;
};

/** Review progress across all guidelines in an evaluation payload. */
export interface ReviewProgress {
  /** Total number of guidelines. */
  total: number;
  /** Number of guidelines that are fully reviewed. */
  completed: number;
  /** Fraction (0..1) of guidelines fully reviewed; 0 when there are none. */
  fraction: number;
  /** True when there is at least one guideline and all are fully reviewed. */
  isComplete: boolean;
  /**
   * True once any review work has begun (a criterion feedback or a guideline
   * verdict recorded). Lets callers show a "start" affordance for an untouched
   * evaluation instead of a 0% progress indicator.
   */
  hasStarted: boolean;
  /**
   * True when any guideline carries a Human "Met" verdict. One Human-Met
   * guideline already settles the order as Met, so callers (e.g. the
   * single-order action) surface a Met tag instead of the progress ring.
   */
  anyHumanMet: boolean;
  /** Order-level evaluation result, surfaced once the review is complete. */
  orderResult: EvaluationResult | null;
}

/**
 * Computes how far along the guideline review is for an evaluation payload.
 *
 * A guideline counts toward `completed` only when {@link isGuidelineFullyReviewed}
 * is true for it, so the fraction reflects fully-reviewed guidelines rather than
 * partial per-criterion progress. `isComplete` flags a fully-reviewed order and
 * `orderResult` carries its order-level verdict for the caller to display.
 *
 * @param payload - The evaluation payload to scan, or null/undefined
 * @returns The {@link ReviewProgress} totals, fraction, and order verdict
 */
export const computeReviewProgress = (
  payload: NCCNEvaluationPayload | null | undefined,
): ReviewProgress => {
  const guidelines = payload?.guidelines ?? [];
  const total = guidelines.length;
  const completed = guidelines.filter(isGuidelineFullyReviewed).length;
  return {
    total,
    completed,
    fraction: total === 0 ? 0 : completed / total,
    isComplete: total > 0 && completed === total,
    hasStarted: guidelines.some(isGuidelineReviewStarted),
    anyHumanMet: hasHumanMetGuideline(payload),
    orderResult: (payload?.order_evaluation_result as EvaluationResult) ?? null,
  };
};

/**
 * Like {@link computeReviewProgress} but only counts the mandatory guidelines:
 * the AI-Met and AI-Undetermined ones. AI-Not-Met guidelines are optional
 * (reviewed only via the override flow), so they are excluded from both the
 * total and the completion gate.
 *
 * @param payload - The evaluation payload to scan, or null/undefined
 * @returns The {@link ReviewProgress} over AI-Met + AI-Undetermined guidelines
 */
export const computeMandatoryReviewProgress = (
  payload: NCCNEvaluationPayload | null | undefined,
): ReviewProgress => {
  const guidelines = payload?.guidelines ?? [];
  const mandatory = guidelines.filter(isMandatoryGuideline);
  const total = mandatory.length;
  const completed = mandatory.filter(isGuidelineFullyReviewed).length;
  return {
    total,
    completed,
    fraction: total === 0 ? 0 : completed / total,
    isComplete: total > 0 && completed === total,
    hasStarted: mandatory.some(isGuidelineReviewStarted),
    anyHumanMet: hasHumanMetGuideline(payload),
    orderResult: (payload?.order_evaluation_result as EvaluationResult) ?? null,
  };
};

/**
 * Value to persist as `review_submitted` on every submit.
 *
 * True once every AI-Met and AI-Undetermined guideline is fully reviewed.
 * Sticky: a later re-review of an already-reviewed Met guideline cannot
 * flip the flag back to false.
 *
 * @param payload - Draft being persisted
 * @param alreadySubmitted - Envelope/local flag from a previous true write
 * @returns Whether the payload should be sent with `review_submitted: true`
 */
export const resolveReviewSubmitted = (
  payload: NCCNEvaluationPayload,
  alreadySubmitted: boolean,
): boolean =>
  alreadySubmitted || computeMandatoryReviewProgress(payload).isComplete;

/**
 * Immutably applies a guideline-level review to a guideline in the payload.
 *
 * @param payload - The current evaluation payload
 * @param guidelineIndex - Index of the guideline being reviewed
 * @param input - The reviewer's guideline-level review
 * @param reviewerEmail - Email of the reviewer, stored as `reviewed_by`
 * @returns A new payload with the guideline's `human_review` updated
 */
export const applyGuidelineReview = (
  payload: NCCNEvaluationPayload,
  guidelineIndex: number,
  input: GuidelineReviewInput,
  reviewerEmail: string,
): NCCNEvaluationPayload => ({
  ...payload,
  guidelines: (payload.guidelines ?? []).map((guideline, gi) => {
    if (gi !== guidelineIndex) return guideline;
    const humanReview: HumanReview = {
      ...(guideline.metadata?.human_review ?? {}),
      verdict: input.verdict,
      verdict_reasoning: input.verdictReasoning,
      ai_improvement_notes: input.aiImprovementNotes,
      reviewed_by: reviewerEmail,
      reviewed_at: new Date().toISOString(),
    };
    return {
      ...guideline,
      metadata: {
        ...guideline.metadata,
        human_review: humanReview,
      },
    };
  }),
});

/**
 * Projects each guideline's human verdict onto its
 * `metadata.evaluation.final_result` (met → `"True"`, not_met → `"False"`),
 * leaving guidelines without a human verdict (or without an evaluation)
 * untouched.
 *
 * The returned payload is intended solely as input to the recompute endpoint so
 * the order-level result reflects the reviewer's verdicts. It must NEVER be
 * persisted: overwriting the AI's `final_result` in storage would make the AI
 * and human verdicts indistinguishable afterwards.
 *
 * @param payload - The evaluation payload to derive from
 * @returns A new payload with human verdicts projected onto `final_result`
 */
export const applyHumanVerdictToFinalResult = (
  payload: NCCNEvaluationPayload,
): NCCNEvaluationPayload => ({
  ...payload,
  guidelines: (payload.guidelines ?? []).map((guideline) => {
    const verdict = guideline.metadata?.human_review?.verdict;
    const evaluation = guideline.metadata?.evaluation;
    if (
      !evaluation ||
      (verdict !== HumanVerdict.Met && verdict !== HumanVerdict.NotMet)
    ) {
      return guideline;
    }
    const finalResult: EvaluationResult =
      verdict === HumanVerdict.Met ? "True" : "False";
    return {
      ...guideline,
      metadata: {
        ...guideline.metadata,
        evaluation: {
          ...evaluation,
          final_result: finalResult,
        },
      },
    };
  }),
});
